import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  INITIAL_WORKSHEET_VIEW_STATE,
  WorksheetViewActionType,
  WorksheetViewState,
  WorksheetViewStateAccessible,
  WorksheetViewStateAction,
  WorksheetViewStateInaccessible,
  WorksheetViewStateType,
} from 'notebook/worksheets/WorksheetConstants';
import {isExecutionEnabled} from 'notebook/worksheets/workspace/WorksheetCapabilityManagementHelpers';
import useReducerWithThunk from 'ui-library/hooks/useReducerWithThunk';
import {assertNever} from 'util/Types';

import {WorksheetViewStateThunk} from './WorksheetViewStateThunkActions';

export function useWorksheetReducer(): [
  WorksheetViewState,
  React.Dispatch<WorksheetViewStateAction | WorksheetViewStateThunk>,
] {
  return useReducerWithThunk<WorksheetViewState, WorksheetViewStateAction>(
    worksheetViewStateReducers,
    INITIAL_WORKSHEET_VIEW_STATE
  );
}

export const worksheetViewStateReducers = (
  state: WorksheetViewState,
  action: WorksheetViewStateAction
): WorksheetViewState => {
  switch (action.type) {
    case WorksheetViewActionType.RELOADING:
      if (isInaccessible(action.worksheet)) {
        return {
          ...state,
          state: WorksheetViewStateType.INACCESSIBLE,
          worksheet: action.worksheet,
        } as WorksheetViewStateInaccessible;
      } else {
        return {
          ...state,
          state: WorksheetViewStateType.RELOADING,
          worksheet: action.worksheet,
          isExecutionEnabled: false,
        } as WorksheetViewStateAccessible;
      }
    case WorksheetViewActionType.LOADED:
      if (isInaccessible(action.worksheet)) {
        return {
          ...state,
          state: WorksheetViewStateType.INACCESSIBLE,
          worksheet: action.worksheet,
        } as WorksheetViewStateInaccessible;
      } else {
        const accessibleState = state as WorksheetViewStateAccessible;
        let newState: WorksheetViewStateAccessible;

        if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
          // Determine the correct state based on whether the step documents have been loaded yet.
          if (action.steps != null) {
            newState = {
              ...accessibleState,
              state: WorksheetViewStateType.LOADED,
              worksheet: action.worksheet,
              steps: action.steps,
            };
          } else {
            newState = {
              ...accessibleState,
              state: WorksheetViewStateType.LOADING_WORKSHEET_STEPS,
              worksheet: action.worksheet,
              steps: null,
            };
          }
        } else {
          newState = {
            ...accessibleState,
            state: WorksheetViewStateType.LOADED,
            worksheet: action.worksheet,
            steps: null,
          };
        }

        // Use the action value of `isExecutionEnabled` if it is provided
        // If not, use the last state of `isExecutionEnabled` if it was enabled before
        // This is needed since we don't always refetch the ProcedureRun when we switch worksheets
        newState.isExecutionEnabled =
          action.isExecutionEnabled ?? accessibleState.isExecutionEnabled ?? false;

        return newState;
      }
    case WorksheetViewActionType.PROCEDURE_LOADED:
      assert(state.state === WorksheetViewStateType.LOADED, 'The Procedure was loaded before the Worksheet');
      if (action.procedureType == null) {
        return {
          ...state,
          isExecutionEnabled: true,
        };
      } else {
        return {
          ...state,
          isExecutionEnabled: isExecutionEnabled(action.procedureType),
        };
      }
    case WorksheetViewActionType.FAILED_TO_LOAD_STEPS:
      return {
        ...state,
        state: WorksheetViewStateType.ERROR_LOADING_WORKSHEET_STEPS,
        steps: null,
      } as WorksheetViewStateAccessible;
    default:
      assert(false, `Unexpected action type ${assertNever(action)}`);
  }
};
